import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, Lock } from 'lucide-react';
import { formatPercentage } from '@/lib/utils/format';

interface MarginIndicatorProps {
  actualMarginPercentage: number;
  targetMargin: number;
  floorMargin: number;
  requiresApproval: boolean;
  isBlocked: boolean;
  blockReason?: string;
}

export function MarginIndicator({
  actualMarginPercentage,
  targetMargin,
  floorMargin,
  requiresApproval,
  isBlocked,
  blockReason,
}: MarginIndicatorProps) {
  const getStatus = () => {
    if (isBlocked) {
      return {
        icon: Lock,
        color: 'text-destructive',
        bgColor: 'bg-destructive/10',
        badge: 'destructive' as const,
        label: 'BLOQUEADO',
        message: blockReason || 'Margen por debajo del piso',
      };
    }

    if (requiresApproval) {
      return {
        icon: AlertTriangle,
        color: 'text-alerta',
        bgColor: 'bg-alerta-suave',
        badge: 'warning' as const,
        label: 'REQUIERE APROBACIÓN',
        message: `El margen está por debajo del objetivo (${targetMargin}%)`,
      };
    }

    return {
      icon: CheckCircle,
      color: 'text-ok',
      bgColor: 'bg-ok-suave',
      badge: 'success' as const,
      label: 'APROBADO AUTOMÁTICAMENTE',
      message: `El margen cumple con el objetivo (${targetMargin}%)`,
    };
  };

  const status = getStatus();
  const Icon = status.icon;

  return (
    <Card
      className={`border-2 ${isBlocked ? 'border-destructive' : requiresApproval ? 'border-alerta' : 'border-ok'}`}
    >
      <CardContent className="p-6">
        <div className="space-y-4">
          {/* Status Badge */}
          <div className="flex items-center justify-between">
            <Badge variant={status.badge} className="text-sm px-3 py-1">
              {status.label}
            </Badge>
            <Icon className={`w-6 h-6 ${status.color}`} />
          </div>

          {/* Margin Display */}
          <div className="space-y-2">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold">
                {formatPercentage(actualMarginPercentage / 100)}
              </span>
              <span className="text-muted-foreground">Margen Real</span>
            </div>

            {/* Progress Bar */}
            <div className="relative h-3 bg-muted rounded-full overflow-hidden">
              {/* Floor Marker */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-destructive z-10"
                style={{ left: `${(floorMargin / 100) * 100}%` }}
              />
              {/* Target Marker */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-ok z-10"
                style={{ left: `${(targetMargin / 100) * 100}%` }}
              />
              {/* Actual Progress */}
              <div
                className={`h-full transition-all ${
                  isBlocked ? 'bg-destructive' : requiresApproval ? 'bg-alerta' : 'bg-ok'
                }`}
                style={{ width: `${Math.min(actualMarginPercentage, 100)}%` }}
              />
            </div>

            {/* Legend */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-destructive rounded-full" />
                Piso: {formatPercentage(floorMargin / 100)}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-ok rounded-full" />
                Objetivo: {formatPercentage(targetMargin / 100)}
              </span>
            </div>
          </div>

          {/* Message */}
          <div className={`p-3 rounded-lg ${status.bgColor}`}>
            <p className={`text-sm ${status.color} font-medium`}>{status.message}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default MarginIndicator;
